import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { FilmStrip, Envelope, LockKey } from '@phosphor-icons/react';
import { isNativeApp } from '../lib/native';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Login = ({ setUser }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, formData);
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setUser(response.data.user);
      toast.success('Welcome back!');
      
      if (response.data.user.role === 'admin') {
        navigate('/admin-dashboard');
      } else if (response.data.user.role === 'producer') {
        navigate('/producer-dashboard');
      } else if (response.data.user.role === 'fan') {
        navigate('/fan-dashboard');
      } else {
        navigate('/investor-dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen bg-background flex items-center justify-center px-4${isNativeApp() ? ' native-auth-screen' : ''}`}>
      <div className="noise-overlay" />
      
      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center space-x-3 mb-6">
            <img src="/fanzingy-logo.jpeg" alt="FanZingy" className="h-10 w-10 rounded-full object-cover ring-1 ring-white/10" data-testid="brand-logo" />
            <span className="font-heading text-2xl font-bold tracking-tight">Fan<span className="text-gold">Zingy</span></span>
          </div>
          <h1 className="font-heading text-4xl font-bold mb-2">WELCOME BACK</h1>
          <p className="font-body text-slate-400">Login to access your portfolio</p>
        </div>

        <form onSubmit={handleSubmit} data-testid="login-form" className="bg-card border border-white/5 rounded-lg p-8">
          <div className="space-y-6">
            <div>
              <label className="font-body text-sm font-medium mb-2 block" htmlFor="email">
                Email
              </label>
              <div className="relative">
                <Envelope size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  data-testid="login-email-input"
                  id="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full bg-black/20 border border-white/10 focus:border-gold/50 focus:ring-1 focus:ring-gold/50 rounded-sm h-12 text-white placeholder:text-slate-400 pl-12 pr-4 font-body outline-none transition-colors"
                  placeholder="your@email.com"
                />
              </div>
            </div>

            <div>
              <label className="font-body text-sm font-medium mb-2 block" htmlFor="password">
                Password
              </label>
              <div className="relative">
                <LockKey size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  data-testid="login-password-input"
                  id="password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full bg-black/20 border border-white/10 focus:border-gold/50 focus:ring-1 focus:ring-gold/50 rounded-sm h-12 text-white placeholder:text-slate-400 pl-12 pr-4 font-body outline-none transition-colors"
                  placeholder="Enter your password"
                />
              </div>
            </div>

            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-3.5"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </div>

          <div className="mt-4 text-center">
            <Link to="/forgot-password" data-testid="login-forgot-link" className="font-body text-sm text-slate-400 hover:text-gold transition-colors">
              Forgot password?
            </Link>
          </div>

          <div className="mt-6 text-center">
            <p className="font-body text-slate-400">
              Don't have an account?{' '}
              <Link to="/register" data-testid="login-register-link" className="text-gold hover:text-gold-hover font-medium transition-colors">
                Register
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;